// ============================================================
// frontend/src/lib/api.ts
// ============================================================
// Thin typed wrapper around fetch().
//
// Each method is generic: pass the expected response type as a
// type parameter, and TypeScript infers the return type:
//
//   const { clients } = await api.get<ClientListResponse>('/clients');
//
// Centralizes:
//   - credentials: include (sends session cookie automatically)
//   - JSON parsing
//   - Error handling (throws Error with .status and .body on non-2xx)
//   - 401 detection (dispatches global 'auth:expired' event)
// ============================================================

const BASE = '/api';

/** Custom error class so callers can read .status and .body. */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = {
    method,
    credentials: 'include',                       // send session cookie
    headers: { 'Content-Type': 'application/json' },
  };
  if (body !== undefined) {
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(BASE + path, opts);

  // Global 401 handling: dispatch event so App can show login screen.
  // We skip /auth/me and /auth/login because those endpoints legitimately
  // return 401 in normal flow and we don't want to flash a "session expired" UX.
  if (res.status === 401 && path !== '/auth/me' && !path.startsWith('/auth/login')) {
    window.dispatchEvent(new CustomEvent('auth:expired'));
  }

  // Parse JSON if present, otherwise null.
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const message = (data && typeof data === 'object' && 'error' in data)
      ? String(data.error)
      : `HTTP ${res.status}`;
    throw new ApiError(message, res.status, data);
  }
  return data as T;
}

export const api = {
  get:  <T>(path: string)              => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put:  <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del:  <T>(path: string)              => request<T>('DELETE', path),
};
